<template>
    <div class="uik-table">
        <div
            class="uik-table__wrapper"
            :class="{'uik-table--loading': getLoading}"
        >
            <div v-if="$slots.header" class="uik-table__header">
                <slot name="header"/>
            </div>
            <div
                :style="maxHeight && !overflow ? ('max-height: ' + maxHeight + 'px;') : ''"
                :class="{'uik-table__container--overflow': overflow}"
                class="uik-table__container"
            >
                <table class="uik-table__table">
                    <slot/>
                </table>
            </div>
        </div>
        <UikPaginate
            v-if="resource"
            :resource="resource"
            @page-change="$emit('page-change', $event)"
        />
    </div>
</template>

<script>
import UikPaginate from "./Paginate"
export default {
    name: "UikTable",
    components: { UikPaginate },
    props: {
        /**
         * Defines maximum height of table
         */
        maxHeight: { type: String, default: "" },
        /**
         * Disables scroll inside table - shows overflow
         */
        overflow: { type: Boolean, default: false },
        /**
         * Is component loading
         */
        isLoading: { type: [Boolean, null], default: null },
        /**
         * Array of selected items
         * Activates selection checkboxes in both head and body
         */
        select: { type: Array, default: null },
        /**
         * The resource this table is showing data for
         */
        resource: { type: [Object, null], default: null },
        /**
         * The module namespace under which the filters will be saved (route name by default)
         */
        filterModuleNamespace: { type: [String, null], default: null }
    },
    data () {
        return {
            selectOptions: []
        }
    },
    provide () {
        return {
            resource: this.resource,
            filterModuleNamespace: this.filtersModuleNamespace,
            selectedItems: this.select,
            selectOptions: this.selectOptions
        }
    },
    computed: {
        /**
         * Gets the active loading state from the prop or the resource list loader
         */
        getLoading () {
            if (this.isLoading !== null) return this.isLoading
            // Support for legacy and new resources
            // TODO: Deprecate in version 1.0
            if (this.resource) return this.resource.loaders.list || this.resource.loaders.getList
            return !!this.isLoading
        }
    }
}
</script>

<style lang="scss">
.uik-table {
    padding: 20px 25px;
    width: 100%;
    background: transparent;
    .uik-table__wrapper {
        width: 100%;
        max-width: 1500px;
        margin: 0 auto;
        border-radius: $borderRadius;
        box-shadow: $boxShadow;
        border: $borderDefault;
        background: white;
        transition: all 0.5s;
        .uik-table__header {
            padding: 20px 30px;
            display: flex;
            flex-flow: row nowrap;
            justify-content: space-between;
            align-items: center;
            border-bottom: $borderDefault;
            .uik-headline__wrapper {
                font-size: 1.125rem;
                line-height: 1.75rem;
                font-weight: 500;
                & + * {
                    margin-top: 0;
                }
            }
        }
        .uik-table__container {
            overflow: auto;
            width: 100%;
            margin: 0;
            border-radius: $borderRadius;
            &::-webkit-scrollbar-track {
                display: initial;
            }
            &::-webkit-scrollbar {
                display: initial;
            }
            .uik-table__table {
                border-collapse: collapse;
                width: 100%;
                margin: 0;
                padding: 0;
                border: none;
                outline: none;
                background: transparent;
                position: relative;
            }
            &--overflow {
                overflow: visible;
                &::-webkit-scrollbar-track {
                    display: none;
                }
                &::-webkit-scrollbar {
                    display: none;
                }
            }
        }
        &.uik-table--loading {
            box-shadow: none;
            border-color: rgba($borderColor, 0.65);
            pointer-events: none;
            position: relative;
            .uik-table-head {
                position: relative;
                &::before {
                    content: "";
                    position: absolute;
                    width: 0%;
                    height: 2px;
                    background: $primary;
                    top: 0px;
                    left: 0;
                    right: 0;
                    margin: 0 auto;
                    transition: all 0.25s;
                    z-index: 3;
                    animation: table-loading-line 3s linear infinite;
                    @keyframes table-loading-line {
                        0% {
                            width: 0%;
                            margin-left: 0;
                        }
                        50% {
                            margin-left: 0;
                            width: 100%;
                        }
                        100% {
                            margin-left: 100%;
                            width: 0%;
                        }
                    }
                }
            }
            &::before {
                position: absolute;
                content: "";
                width: 50%;
                height: 100%;
                top: 0;
                left: 0;
                background: linear-gradient(90deg, rgba(white, 0), rgba(white, 0.65), rgba(white, 0));
                z-index: 5;
                animation: table-loading-shimmer 2.5s ease-in-out infinite;
                @keyframes table-loading-shimmer {
                    0% {
                        opacity: 0;
                        margin-left: -50%;
                    }
                    50% {
                        opacity: 1;
                    }
                    100% {
                        opacity: 0;
                        margin-left: 100%;
                    }
                }
            }
            .uik-table-row {
                opacity: 0.25;
            }
        }
    }
}
</style>
